import apiClient from '../config/api';

// Menu Service for handling menu-related API calls
class MenuService {
  
  // Get daily menu based on current calendar day (1-31)
  async getDailyMenu(selectedDate = null) {
    try {
      // Use provided date or default to current date
      const targetDate = selectedDate || new Date();
      
      const response = await apiClient.get('/menu');
      
      // Filter items for specified day of month
      if (response.data && response.data.length > 0) {
        const dailyMenu = this.filterMenuBySpecificDay(response.data, targetDate);
        
        // Se não encontrou itens para o dia específico, retornar vazio
        if (dailyMenu.length === 0) {
          return [];
        }
        
        return dailyMenu;
      }
      
      return [];
    } catch (error) {
      console.error('❌ MenuService: Error fetching daily menu:', error);
      // Return empty menu structure if API fails
      return [];
    }
  }

  // Get weekly menu with daily rotation (legacy method)
  async getWeeklyMenu(weekOffset = 0) {
    try {
      // console.log('MenuService: getWeeklyMenu called with weekOffset:', weekOffset);
      const params = { weekOffset: weekOffset.toString() };
      // console.log('MenuService: API params:', params);
      // console.log('MenuService: Making request to /menu/weekly with params:', params);
      const response = await apiClient.get('/menu/weekly', params);
      
      // The /weekly endpoint returns the enhanced menu directly
      if (response && Object.keys(response).length > 0) {
        return response;
      }
      
      // Don't return fallback for future weeks (weekOffset > 0)
      if (weekOffset > 0) {
        // console.log('⚠️ No data for future week, returning empty object');
        return {};
      }
      
      return {};
    } catch (error) {
      console.error('❌ MenuService: Error fetching weekly menu:', error);
      
      // Don't return fallback for future weeks (weekOffset > 0)
      if (weekOffset > 0) {
        // console.log('⚠️ API error for future week, returning empty object');
        return {};
      }
      
      // Return empty menu structure only for current/past weeks
      return {};
    }
  }

  // Get weekly menu (rotation) and augment with items scheduled for the same day
  async getWeeklyMenuAugmented(weekOffset = 0) {
    try {
      // Do not use legacy baseWeekly to ensure consistency with Daily view
      // const baseWeekly = await this.getWeeklyMenu(weekOffset);
      
      // Fetch all available items to augment per actual calendar day
      const allResp = await apiClient.get('/menu');
      const allItems = (allResp && allResp.data) ? allResp.data : Array.isArray(allResp) ? allResp : [];

      const days = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
      const today = new Date();
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(today.getDate() - today.getDay());
      startOfCurrentWeek.setHours(0, 0, 0, 0);
      const startOfTargetWeek = new Date(startOfCurrentWeek);
      startOfTargetWeek.setDate(startOfCurrentWeek.getDate() + (weekOffset * 7));

      // Helpers
      // const removeAccents = (str) => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      // const normalizeDay = (val) => {
      //   if (!val) return '';
      //   let s = removeAccents(String(val).toLowerCase().trim());
      //   s = s.replace(/-feira/g, '').replace(/\s*feira/g, '').replace(/\s+/g, '');
      //   const map = {
      //     domingo: 'domingo', dom: 'domingo', sunday: 'domingo',
      //     segunda: 'segunda', seg: 'segunda', monday: 'segunda',
      //     terca: 'terca', ter: 'terca', tuesday: 'terca',
      //     quarta: 'quarta', qua: 'quarta', wednesday: 'quarta',
      //     quinta: 'quinta', qui: 'quinta', thursday: 'quinta',
      //     sexta: 'sexta', sex: 'sexta', friday: 'sexta',
      //     sabado: 'sabado', sab: 'sabado', saturday: 'sabado'
      //   };
      //   return map[s] || s;
      // };
      const parsePrepDate = (raw) => {
        if (!raw) return null;
        if (raw instanceof Date && !isNaN(raw.getTime())) return raw;
        if (typeof raw === 'number') {
          const d = new Date(raw);
          return isNaN(d.getTime()) ? null : d;
        }
        if (typeof raw === 'string') {
          const s = raw.trim();
          if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
            const d = new Date(s);
            return isNaN(d.getTime()) ? null : d;
          }
          const m = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
          if (m) {
            const dd = Number(m[1]);
            const mm = Number(m[2]) - 1;
            const yyyy = Number(m[3]);
            const d = new Date(yyyy, mm, dd);
            return isNaN(d.getTime()) ? null : d;
          }
          const d = new Date(s);
          return isNaN(d.getTime()) ? null : d;
        }
        return null;
      };
      const sameCalendarDate = (a, b) => {
        if (!a || !b) return false;
        // Compare somente em UTC para evitar deslocamento de dia por fuso
        return (
          a.getUTCDate() === b.getUTCDate() &&
          a.getUTCMonth() === b.getUTCMonth() &&
          a.getUTCFullYear() === b.getUTCFullYear()
        );
      };

      const combined = {};
      days.forEach((day, idx) => {
        const dayDate = new Date(startOfTargetWeek);
        dayDate.setDate(startOfTargetWeek.getDate() + idx);
        dayDate.setHours(0, 0, 0, 0);

        const merged = [];
        const existingIds = new Set();

        // Filter items scheduled for this calendar day, strictly by date (like getDailyMenu)
        // Ignoring items that rely solely on dayOfWeek to prevent ghost items
        const extras = allItems.filter(item => {
          const prepRaw = item.dataPreparacao;
          const prepDate = parsePrepDate(prepRaw);

          if (prepDate) {
            // Quando há data, basta comparar a data em UTC
            return sameCalendarDate(prepDate, dayDate);
          }

          // If no date, ignore it to match getDailyMenu behavior
          return false;
        }).map(i => this.formatMenuItem(i));

        extras.forEach(extra => {
          const id = extra.id || extra._id;
          if (!existingIds.has(id)) {
            merged.push(extra);
            existingIds.add(id);
          }
        });

        combined[day] = merged;
      });

      return combined;
    } catch (error) {
      console.error('❌ MenuService: Error fetching augmented weekly menu:', error);
      return {};
    }
  }

  // Get weekly menu with ALL available items (shows all dishes, not just selected ones)
  async getWeeklyMenuAll() {
    try {
      console.log('MenuService: getWeeklyMenuAll called - fetching all available items');
      const response = await apiClient.get('/menu/weekly-all');
      
      if (response && Object.keys(response).length > 0) {
        console.log('MenuService: Successfully retrieved all weekly menu items');
        return response;
      }
      
      console.log('MenuService: No data returned from /weekly-all, using fallback');
      return {};
    } catch (error) {
      console.error('❌ MenuService: Error fetching all weekly menu items:', error);
      return {};
    }
  }

  // Get weekly menu grouped by day-of-week using day endpoint
  async getWeeklyMenuByDays() {
    try {
      const days = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
      const requests = await Promise.all(days.map(day => this.getDayMenu(day)));
      const weekly = {};
      days.forEach((day, idx) => {
        const resp = requests[idx];
        // The /menu/day endpoint returns { success, data }
        // Fallbacks may return an array directly
        if (resp && Array.isArray(resp)) {
          weekly[day] = resp;
        } else if (resp && resp.data) {
          weekly[day] = resp.data;
        } else {
          weekly[day] = [];
        }
      });
      return weekly;
    } catch (error) {
      console.error('❌ MenuService: Error fetching weekly menu by days:', error);
      return {};
    }
  }

  // Get today's special dish
  async getTodaysSpecial() {
    try {
      const response = await apiClient.get('/menu/daily/special');
      return response.data;
    } catch (error) {
      console.error('Error fetching today\'s special:', error);
      return null;
    }
  }

  // Get monthly rotation schedule
   async getMonthlyRotation(month, year) {
     try {
       const params = {};
       if (month !== undefined) params.month = month;
       if (year !== undefined) params.year = year;
       
       const response = await apiClient.get('/menu/daily/monthly-calendar', { params });
       return response.data;
     } catch (error) {
       console.error('Error fetching monthly rotation:', error);
       return {};
     }
   }

  // Get rotation statistics
  async getRotationStats() {
    try {
      const response = await apiClient.get('/menu/daily/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching rotation stats:', error);
      return null;
    }
  }

  // Get menu for specific day
  async getDayMenu(dayOfWeek) {
    try {
      const validDays = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
      if (!validDays.includes(dayOfWeek)) {
        throw new Error('Dia da semana inválido.');
      }

      return await apiClient.get(`/menu/day/${dayOfWeek}`);
    } catch (error) {
      console.error('Error fetching day menu:', error);
      return [];
    }
  }

  // Search menu items
  async searchMenuItems(query, filters = {}) {
    try {
      const params = {
        q: query,
        ...filters
      };
      
      return await apiClient.get('/menu/search', params);
    } catch (error) {
      console.error('Error searching menu items:', error);
      throw new Error('Falha na busca do cardápio. Tente novamente.');
    }
  }

  // Get menu by category
  async getMenuByCategory(category) {
    try {
      const validCategories = ['prato principal', 'arroz', 'feijao', 'massa', 'salada', 'guarnição', 'sobremesa', 'suco'];
      if (!validCategories.includes(category)) {
        throw new Error('Categoria inválida.');
      }

      return await apiClient.get('/menu/category', { category });
    } catch (error) {
      console.error('Error fetching menu by category:', error);
      throw new Error('Falha ao carregar cardápio por categoria.');
    }
  }

  // Get dietary restriction menu
  async getDietaryMenu(restrictions = []) {
    try {
      const params = {};
      
      if (restrictions.includes('vegetarian')) {
        params.isVegetarian = true;
      }
      if (restrictions.includes('vegan')) {
        params.isVegan = true;
      }
      if (restrictions.includes('glutenFree')) {
        params.isGlutenFree = true;
      }

      return await apiClient.get('/menu/dietary', params);
    } catch (error) {
      console.error('Error fetching dietary menu:', error);
      throw new Error('Falha ao carregar cardápio com restrições alimentares.');
    }
  }

  // Get special offers
  async getSpecialOffers() {
    try {
      return await apiClient.get('/menu/specials');
    } catch (error) {
      console.error('Error fetching special offers:', error);
      return [];
    }
  }

  // Get menu item details
  async getMenuItem(itemId) {
    try {
      return await apiClient.get(`/menu/item/${itemId}`);
    } catch (error) {
      console.error('Error fetching menu item:', error);
      throw new Error('Falha ao carregar detalhes do item.');
    }
  }

  // Fallback weekly menu (used when API is unavailable)
  getFallbackWeeklyMenu() {
    return {
      domingo: [
        {
          id: 'fallback-7',
          name: 'Especial do Final de Semana',
          description: 'Cardápio em atualização',
          category: 'especial',
          isAvailable: true
        }
      ],
      segunda: [
        {
          id: 'fallback-1',
          name: 'Prato do Dia',
          description: 'Cardápio em atualização',
          category: 'prato-principal',
          isAvailable: true
        }
      ],
      terca: [
        {
          id: 'fallback-2',
          name: 'Prato do Dia',
          description: 'Cardápio em atualização',
          category: 'prato-principal',
          isAvailable: true
        }
      ],
      quarta: [
        {
          id: 'fallback-3',
          name: 'Prato do Dia',
          description: 'Cardápio em atualização',
          category: 'prato-principal',
          isAvailable: true
        }
      ],
      quinta: [
        {
          id: 'fallback-4',
          name: 'Prato do Dia',
          description: 'Cardápio em atualização',
          category: 'prato-principal',
          isAvailable: true
        }
      ],
      sexta: [
        {
          id: 'fallback-5',
          name: 'Prato do Dia',
          description: 'Cardápio em atualização',
          category: 'prato-principal',
          isAvailable: true
        }
      ],
      sabado: [
        {
          id: 'fallback-6',
          name: 'Especial do Final de Semana',
          description: 'Cardápio em atualização',
          category: 'especial',
          isAvailable: true
        }
      ]
    };
  }

  // Fallback day menu
  getFallbackDayMenu(dayOfWeek) {
    const fallbackMenu = this.getFallbackWeeklyMenu();
    return fallbackMenu[dayOfWeek] || [];
  }

  // Utility method to format menu items for display
  formatMenuItems(items) {
    return items.map(item => ({
      ...item,
      formattedPreparationTime: item.preparationTime 
        ? `${item.preparationTime} min`
        : 'Tempo variável',
      dietaryInfo: this.getDietaryInfo(item)
    }));
  }

  // Get dietary information for an item
  getDietaryInfo(item) {
    const info = [];
    if (item.isVegan) info.push('Vegano');
    else if (item.isVegetarian) info.push('Vegetariano');
    if (item.isGlutenFree) info.push('Sem Glúten');
    return info;
  }

  // Group menu items by day of the week
  // Filter menu items for specific calendar day (1-31)
  filterMenuBySpecificDay(items, targetDate) {
    // Use UTC to avoid timezone issues
    const targetDay = targetDate.getUTCDate(); // Get target day of month (1-31) in UTC
    const targetMonth = targetDate.getUTCMonth(); // Get target month (0-11) in UTC
    const targetYear = targetDate.getUTCFullYear(); // Get target year in UTC
    
    const dayItems = [];
    
    items.forEach((item, index) => {
      const formattedItem = this.formatMenuItem(item);
      
      // Check if item has a preparation date that matches target day, month and year
      if (item.dataPreparacao) {
        const prepDate = new Date(item.dataPreparacao);
        // Use UTC methods to avoid timezone conversion issues
        const itemDay = prepDate.getUTCDate();
        const itemMonth = prepDate.getUTCMonth();
        const itemYear = prepDate.getUTCFullYear();
        
        // If item matches target day, month and year, add it to the menu
        if (itemDay === targetDay && itemMonth === targetMonth && itemYear === targetYear) {
          dayItems.push(formattedItem);
        }
      }
    });
    
    // If no items found for target day, return empty
    if (dayItems.length === 0) {
      // console.log(`⚠️ No items found for day ${targetDay}`);
      return [];
    }
    
    // console.log(`📋 Daily menu (day ${targetDay}): ${dayItems.length} items`);
    return dayItems;
  }

  // Format a single menu item
  formatMenuItem(item) {
    return {
      ...item,
      // Map database fields to display fields
      id: item._id || item.id,
      name: item.nome || item.name,
      description: item.descricao || item.description,
      category: item.categoria || item.category,
      unit: item.unidade || item.unit,
      isAvailable: item.disponivel !== undefined ? item.disponivel : (item.isAvailable !== undefined ? item.isAvailable : true),
      image: item.imagem || item.image,
      dataPreparacao: item.dataPreparacao,
      dayOfWeek: item.diaDaSemana || item.dayOfWeek
    };
  }

  // Get fallback daily menu
  getFallbackDailyMenu() {
    const currentDay = new Date().getDate();
    // console.log(`🔄 Using fallback menu for day ${currentDay}`);
    
    // Simple fallback based on day of month
    const fallbackItems = [
      {
        id: `fallback-${currentDay}-1`,
        name: `Prato do Dia ${currentDay}`,
        description: 'Deliciosa refeição preparada especialmente para hoje',
        category: 'prato-principal',
        price: 25.90,
        preparationTime: 30,
        calories: 450,
        ingredients: ['Ingredientes frescos', 'Temperos especiais'],
        isVegetarian: false,
        isVegan: false,
        isGlutenFree: false,
        isAvailable: true,
        specialOffer: false,
        tags: ['especial-do-dia'],
        formattedPreparationTime: '30 min',
        formattedPrice: 'R$ 25,90',
        dietaryInfo: []
      }
    ];
    
    return fallbackItems;
  }

  groupMenuItemsByDay(items) {
    const daysOfWeek = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
    const groupedMenu = {};
    
    // Initialize each day with empty array
    daysOfWeek.forEach(day => {
      groupedMenu[day] = [];
    });
    
    // Group items by their diaDaSemana field
    items.forEach((item, index) => {
      const formattedItem = this.formatMenuItem(item);
      
      // Use diaDaSemana field to group items properly
      const dayOfWeek = item.diaDaSemana || item.dayOfWeek;
      if (dayOfWeek && daysOfWeek.includes(dayOfWeek)) {
        groupedMenu[dayOfWeek].push(formattedItem);
      } else {
        console.warn(`Item "${item.nome || item.name}" has invalid or missing diaDaSemana: ${dayOfWeek}`);
        // Only fallback to round-robin if no valid day is specified
        const dayIndex = index % daysOfWeek.length;
        groupedMenu[daysOfWeek[dayIndex]].push(formattedItem);
      }
    });
    
    // console.log('📋 Grouped menu by day:', Object.keys(groupedMenu).map(day => `${day}: ${groupedMenu[day].length} items`).join(', '));
    return groupedMenu;
  }
}

// Create singleton instance
const menuService = new MenuService();

export default menuService;
export { MenuService };